import { Page } from "@models/tracking/entities";
import {
  DeauthenticateUser,
  FilterDashboard,
  FirstEntry,
  TrackAiCurriculumRecommendationFeedback,
  TrackLaunchTrekButtonClick,
  TrackMissionControl,
  TrackMissionControlTabClick,
  TrackSetupChecklistActionLaunched,
  TrackSetupChecklistAttestationCleared,
  TrackSetupChecklistAttestationRecorded,
  TrackSetupChecklistItemCompleted,
  TrackSetupChecklistQuestionAnswered,
  TrackSetupChecklistStepExpanded,
  TrackSetupChecklistViewed,
  TrackTestYourNetworkConnectivityClick,
} from "@models/tracking/events";
import {
  ActivityPlayerViewedEvent,
  AuthenticatedEvent,
  ButtonClickedEvent,
  ClassroomArchivedEvent,
  ClassroomCreatedEvent,
  ClassroomDeletedEvent,
  ClassroomPageViewedEvent,
  ClassroomReactivatedEvent,
  ClassroomStatusFilterSelectedEvent,
  CredentialsExportedEvent,
  InstructorAssignedToClassroomEvent,
  LearnerArchivedEvent,
  LearnerAssignedToClassroomEvent,
  LearnerReactivatedEvent,
  NextActivityButtonClickedEvent,
  PreviewClassroomButtonClickedEvent,
  ProgramAssignedToClassroomEvent,
  ProgramAssignedToOrganizationEvent,
  SimPassCreatedEvent,
  SoleClassroomWarningSelectedEvent,
  SoleClassroomWarningViewedEvent,
  StorylineActivityCompletedEvent,
  StorylineActivityStartedEvent,
  StorylineSlideViewedEvent,
} from "@models/tracking/xapi-events";
import container from "@src/container";

const { snowplowTrackingService, xApiTrackingService } = container;

/**
 * Snowplow tracking functions
 */

export const trackLogout = (logoutType) => {
  if (!logoutType) return;
  snowplowTrackingService.trackEvent(new DeauthenticateUser(logoutType));
};

/**
 * Sets the user on the tracker first: on the login page it has none yet.
 */
export const trackFirstEntry = (user) => {
  snowplowTrackingService.setUser(user);
  snowplowTrackingService.trackEvent(new FirstEntry());
};

export const trackView = (viewClass, pageName, ...args) => {
  const event = new viewClass(...args);
  snowplowTrackingService.trackEvent(event, [new Page(pageName)]);
};

export const trackFilterEvent = (startDate, endDate) => {
  snowplowTrackingService.trackEvent(new FilterDashboard(startDate, endDate));
};

export const trackMissionControl = (viewedUserId, viewedClassroomId) => {
  snowplowTrackingService.trackEvent(
    new TrackMissionControl(viewedUserId, viewedClassroomId)
  );
};

export const trackMissionControlTabClick = (classroomId) => {
  snowplowTrackingService.trackEvent(
    new TrackMissionControlTabClick(classroomId)
  );
};

export const trackLaunchTrekButtonClick = () => {
  snowplowTrackingService.trackEvent(new TrackLaunchTrekButtonClick());
};

export const trackTestYourNetworkConnectivityClick = () => {
  snowplowTrackingService.trackEvent(
    new TrackTestYourNetworkConnectivityClick()
  );
};

export const trackAiCurriculumRecommendationFeedback = (
  rating,
  entryPoint,
  sessionId,
  runVersion
) => {
  snowplowTrackingService.trackEvent(
    new TrackAiCurriculumRecommendationFeedback(
      rating,
      entryPoint,
      sessionId,
      runVersion
    )
  );
};

export const trackSetupChecklistViewed = (organizationCode) => {
  snowplowTrackingService.trackEvent(
    new TrackSetupChecklistViewed(organizationCode)
  );
};

export const trackSetupChecklistStepExpanded = (organizationCode, stepKey) => {
  snowplowTrackingService.trackEvent(
    new TrackSetupChecklistStepExpanded(organizationCode, stepKey)
  );
};

export const trackSetupChecklistItemCompleted = (organizationCode, item) => {
  snowplowTrackingService.trackEvent(
    new TrackSetupChecklistItemCompleted(organizationCode, item)
  );
};

export const trackSetupChecklistAttestationRecorded = (
  organizationCode,
  key
) => {
  snowplowTrackingService.trackEvent(
    new TrackSetupChecklistAttestationRecorded(organizationCode, key)
  );
};

export const trackSetupChecklistAttestationCleared = (
  organizationCode,
  key
) => {
  snowplowTrackingService.trackEvent(
    new TrackSetupChecklistAttestationCleared(organizationCode, key)
  );
};

export const trackSetupChecklistQuestionAnswered = (
  organizationCode,
  key,
  option
) => {
  snowplowTrackingService.trackEvent(
    new TrackSetupChecklistQuestionAnswered(organizationCode, key, option)
  );
};

export const trackSetupChecklistActionLaunched = (organizationCode, action) => {
  snowplowTrackingService.trackEvent(
    new TrackSetupChecklistActionLaunched(organizationCode, action)
  );
};

/**
 * xAPI tracking functions
 */

export const trackAuthenticated = async (userId) => {
  await xApiTrackingService.trackEvent(new AuthenticatedEvent(userId));
};

export const trackProgramAssignedToOrganization = async (
  programId,
  programName,
  organizationId
) => {
  await xApiTrackingService.trackEvent(
    new ProgramAssignedToOrganizationEvent(
      programId,
      programName,
      organizationId
    )
  );
};

export const trackInstructorAssignedToClassroom = async (
  instructorUserId,
  classroomId
) => {
  await xApiTrackingService.trackEvent(
    new InstructorAssignedToClassroomEvent(instructorUserId, classroomId)
  );
};

export const trackInstructorsAssignedToClassroom = async (
  instructorUserIds,
  classroomId
) => {
  await xApiTrackingService.trackEvents(
    instructorUserIds.map(
      (instructorUserId) =>
        new InstructorAssignedToClassroomEvent(instructorUserId, classroomId)
    ),
    true
  );
};

export const trackLearnerAssignedToClassroom = async (
  learnerUserId,
  classroomId
) => {
  await xApiTrackingService.trackEvent(
    new LearnerAssignedToClassroomEvent(learnerUserId, classroomId)
  );
};

export const trackLearnersAssignedToClassroom = async (
  learnerUserIds,
  classroomId
) => {
  await xApiTrackingService.trackEvents(
    learnerUserIds.map(
      (learnerUserId) =>
        new LearnerAssignedToClassroomEvent(learnerUserId, classroomId)
    ),
    true
  );
};

export const trackClassroomCreated = async (
  classroomId,
  classroomName,
  classroomType,
  productList
) => {
  await xApiTrackingService.trackEvent(
    new ClassroomCreatedEvent(
      classroomId,
      classroomName,
      classroomType,
      productList
    )
  );
};

export const trackProgramAssignedToClassroom = async (
  programId,
  programName,
  classroomId
) => {
  await xApiTrackingService.trackEvent(
    new ProgramAssignedToClassroomEvent(programId, programName, classroomId)
  );
};

export const trackSimPassCreated = async (
  learningExperienceId,
  simulationName,
  classroomId,
  productId
) => {
  await xApiTrackingService.trackEvent(
    new SimPassCreatedEvent(
      learningExperienceId,
      simulationName,
      classroomId,
      productId.toString()
    )
  );
};

export const trackActivityPlayerViewed = async (
  pageId,
  pageName,
  classroomId,
  activityType
) => {
  await xApiTrackingService.trackEvent(
    new ActivityPlayerViewedEvent(pageId, pageName, classroomId, activityType)
  );
};

export const trackStorylineActivityStarted = async (
  contentId,
  activityName
) => {
  await xApiTrackingService.trackEvent(
    new StorylineActivityStartedEvent(contentId, activityName)
  );
};

export const trackStorylineActivityCompleted = async (
  contentId,
  objectName,
  success = null,
  completed = null
) => {
  await xApiTrackingService.trackEvent(
    new StorylineActivityCompletedEvent(
      contentId,
      objectName,
      success,
      completed
    )
  );
};

export const trackPreviewClassroomButtonClicked = async (
  classroomId,
  programTemplateId,
  menuTemplateId,
  productId
) => {
  await xApiTrackingService.trackEvent(
    new PreviewClassroomButtonClickedEvent(
      classroomId,
      programTemplateId,
      menuTemplateId,
      productId
    )
  );
};

export const trackNextActivityButtonClicked = async (buttonId, classroomId) => {
  await xApiTrackingService.trackEvent(
    new NextActivityButtonClickedEvent(buttonId, classroomId)
  );
};

export const trackClassroomPageViewed = async (pageId, pageName) => {
  await xApiTrackingService.trackEvent(
    new ClassroomPageViewedEvent(pageId, pageName)
  );
};

export const trackCredentialsExported = async (
  credentialId,
  credentialName,
  classroomId
) => {
  await xApiTrackingService.trackEvent(
    new CredentialsExportedEvent(
      credentialId.toString(),
      credentialName,
      classroomId.toString()
    )
  );
};

export const trackStorylineSlideViewed = async (contentId, slideName) => {
  await xApiTrackingService.trackEvent(
    new StorylineSlideViewedEvent(contentId, slideName)
  );
};

export const trackButtonClicked = async (id, name, options = {}) => {
  await xApiTrackingService.trackEvent(
    new ButtonClickedEvent(id, name, options)
  );
};

export const trackClassroomsArchived = async (classrooms, config) => {
  if (classrooms?.length > 0) {
    const isBatchEvent = classrooms.length > 1;
    const batchId = isBatchEvent ? config.batchId : null;
    const events = classrooms.map(
      (c) =>
        new ClassroomArchivedEvent(
          c.classroomId,
          c.name,
          config.source,
          c.learnersAlsoArchived ?? false,
          c.learnerArchiveCount ?? 0,
          c.rostered ?? config.rostered ?? null,
          batchId,
          isBatchEvent
        )
    );

    return events.length === 1
      ? xApiTrackingService.trackEvent(events[0])
      : xApiTrackingService.trackEvents(events, true);
  }
};

export const trackClassroomsReactivated = async (classrooms, config) => {
  if (classrooms?.length > 0) {
    const isBatchEvent = classrooms.length > 1;
    const batchId = isBatchEvent ? config.batchId : null;
    const events = classrooms.map(
      (c) =>
        new ClassroomReactivatedEvent(
          c.classroomId,
          c.name,
          config.source,
          config.timeInArchiveDays ?? null,
          c.rostered ?? config.rostered ?? null,
          batchId,
          isBatchEvent
        )
    );

    return events.length === 1
      ? xApiTrackingService.trackEvent(events[0])
      : xApiTrackingService.trackEvents(events, true);
  }
};

export const trackClassroomsDeleted = async (classrooms, config) => {
  if (classrooms?.length > 0) {
    const isBatchEvent = classrooms.length > 1;
    const batchId = isBatchEvent ? config.batchId : null;
    const events = classrooms.map(
      (c) =>
        new ClassroomDeletedEvent(
          c.classroomId,
          c.name,
          config.timeInArchiveDays ?? null,
          batchId,
          isBatchEvent
        )
    );

    return events.length === 1
      ? xApiTrackingService.trackEvent(events[0])
      : xApiTrackingService.trackEvents(events, true);
  }
};

export const trackSoleClassroomWarningViewed = async () => {
  await xApiTrackingService.trackEvent(new SoleClassroomWarningViewedEvent());
};

export const trackSoleClassroomWarningSelected = async (
  affectedLearnerCount,
  isBulkOperation,
  userProceeded
) => {
  await xApiTrackingService.trackEvent(
    new SoleClassroomWarningSelectedEvent(
      affectedLearnerCount,
      isBulkOperation,
      userProceeded
    )
  );
};

export const trackClassroomStatusFilterSelected = async (
  filterValue,
  previousFilterValue,
  classroomCountInView
) => {
  await xApiTrackingService.trackEvent(
    new ClassroomStatusFilterSelectedEvent(
      filterValue,
      previousFilterValue,
      classroomCountInView
    )
  );
};

export const trackLearnersArchived = async (users, config) => {
  if (users?.length > 0) {
    const isBatchEvent = users.length > 1;
    const batchId = isBatchEvent ? config.batchId : null;
    const events = users.map(
      (u) =>
        new LearnerArchivedEvent(
          u.userId,
          u.username,
          config.source,
          config.triggeredByClassroomArchive ?? false,
          u.rostered ?? config.rostered ?? null,
          batchId,
          isBatchEvent
        )
    );
    return events.length === 1
      ? xApiTrackingService.trackEvent(events[0])
      : xApiTrackingService.trackEvents(events);
  }
};

export const trackLearnersReactivated = async (users, config) => {
  if (users?.length > 0) {
    const isBatchEvent = users.length > 1;
    const batchId = isBatchEvent ? config.batchId : null;
    const events = users.map(
      (u) =>
        new LearnerReactivatedEvent(
          u.userId,
          u.username,
          config.timeInArchiveDays ?? null,
          batchId,
          isBatchEvent
        )
    );
    return events.length === 1
      ? xApiTrackingService.trackEvent(events[0])
      : xApiTrackingService.trackEvents(events, true);
  }
};

// Outcomes dashboard trackers live in a sibling module and are re-exported here
// so call sites keep importing every tracker from this one facade.
export * from "./event-trackers.outcomes";
